import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { useDropzone } from 'react-dropzone';
import { toast } from 'react-toastify';
import { InvoiceType } from '../../../__generated__/graphql';
import { Button } from '../button';
import { Modal } from '../modal';
import { CHARGE_INCIDENT_FEES } from '../../services/bookingService/queries';
import { GET_PRESIGNED_URL } from '../../services/carService/queries';

const CHARGE_TYPES = [
  { label: 'Refueling', value: InvoiceType.FuelFeeCharge },
  { label: 'Tolls', value: InvoiceType.TollFeeCharge },
  { label: 'Tickets', value: InvoiceType.TicketFeeCharge },
  { label: 'Late Fee', value: InvoiceType.LateFee },
  { label: 'Improper Return', value: InvoiceType.OtherFeeCharge },
  { label: 'Additional Usage', value: InvoiceType.OtherFeeCharge },
  { label: 'Cleaning Fee', value: InvoiceType.CleaningFee },
  { label: 'Smoke Fee', value: InvoiceType.SmokingFee },
  { label: 'Claim Charge', value: InvoiceType.DamageCharge },
  { label: 'Tow Charge', value: InvoiceType.OtherFeeCharge },
];

type SavedLine = {
  incidentType: InvoiceType;
  label: string;
  amount: number;
  description: string;
  photoS3Keys: string[];
  files: File[];
};

export const incidentChargeProjectedTotal = (
  savedTotal: number,
  draftAmount: number | null,
  editingAmount = 0,
) => savedTotal - editingAmount + (draftAmount ?? 0);

export const shouldExposeIncidentPaymentLink = (result?: {
  status?: string;
  paymentLink?: string | null;
}) => result?.status === 'INVOICE_SENT' && !!result.paymentLink;

const Container = styled.div`
  ${tw`flex flex-col gap-4 w-full`}
`;

const Title = styled.h2`
  ${tw`text-xl font-semibold text-text-primary pr-8`}
`;

const Grid = styled.div`
  ${tw`grid gap-2`}
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
`;

const TypeButton = styled.button<{ selected: boolean }>`
  ${tw`rounded-lg border p-3 text-left text-sm font-medium`}
  border-color: ${({ selected }) =>
    selected ? 'var(--color-text-primary)' : 'var(--color-border)'};
  background: ${({ selected }) => (selected ? 'var(--color-text-primary)' : '#fff')};
  color: ${({ selected }) => (selected ? '#fff' : 'var(--color-text-secondary)')};
`;

const Input = styled.input`
  ${tw`w-full p-3 border border-border-default rounded-lg`}
`;

const TextArea = styled.textarea`
  ${tw`w-full p-3 border border-border-default rounded-lg min-h-[110px]`}
`;

const DropzoneContainer = styled.div<{ isDragActive: boolean }>`
  ${tw`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors`}
  border-color: ${({ isDragActive }) => (isDragActive ? '#4F46E5' : 'var(--color-border)')};
  background-color: ${({ isDragActive }) => (isDragActive ? '#EEF2FF' : 'white')};
`;

const PhotoGrid = styled.div`
  ${tw`grid grid-cols-3 gap-2`}
`;

const PhotoPreview = styled.div`
  ${tw`relative aspect-square rounded-lg overflow-hidden border border-border-default`}
`;

const PhotoImg = styled.img`
  ${tw`w-full h-full object-cover`}
`;

const RemoveButton = styled.button`
  ${tw`absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold`}
`;

const Actions = styled.div`
  ${tw`flex flex-col md:flex-row gap-3 justify-end pt-2`}
`;

const LineCard = styled.div`
  ${tw`rounded-lg border border-border-default p-3 flex flex-col gap-2`}
`;

const LineRow = styled.div`
  ${tw`flex items-start justify-between gap-3`}
`;

const ResultBox = styled.div`
  ${tw`rounded-lg border p-4 text-sm flex flex-col gap-2`}
  background: #f0fdf4;
  border-color: #bbf7d0;
`;

const InvoiceSentBox = styled(ResultBox)`
  background: #fffbeb;
  border-color: #fcd34d;
`;

const HelperText = styled.p`
  ${tw`text-sm text-text-secondary`}
`;

interface IncidentChargeModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: any;
  refetchBooking: () => Promise<any> | void;
}

export const IncidentChargeModal: React.FC<IncidentChargeModalProps> = ({
  isOpen,
  onClose,
  booking,
  refetchBooking,
}) => {
  const [savedLines, setSavedLines] = useState<SavedLine[]>([]);
  const [selectedType, setSelectedType] = useState(CHARGE_TYPES[0]);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<string[]>([]);
  const [photoS3Keys, setPhotoS3Keys] = useState<string[]>([]);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [result, setResult] = useState<any>(null);

  const [getPresignedUrl] = useMutation(GET_PRESIGNED_URL);
  const [chargeIncidentFees, { loading: charging }] = useMutation(CHARGE_INCIDENT_FEES);

  const savedTotal = useMemo(
    () => savedLines.reduce((sum, line) => sum + line.amount, 0),
    [savedLines],
  );

  const draftAmount = useMemo(() => {
    const value = Number(amount);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [amount]);

  const draftHasContent = draftAmount != null || description.trim().length > 0 || files.length > 0;

  const editingAmount = editingIndex == null ? 0 : savedLines[editingIndex]?.amount || 0;
  const projectedTotal = incidentChargeProjectedTotal(savedTotal, draftAmount, editingAmount);

  useEffect(() => {
    if (!isOpen) return;
    setSavedLines([]);
    setSelectedType(CHARGE_TYPES[0]);
    setAmount('');
    setDescription('');
    setFiles([]);
    setPreviews([]);
    setPhotoS3Keys([]);
    setEditingIndex(null);
    setResult(null);
  }, [isOpen]);

  useEffect(() => {
    return () => previews.forEach(url => URL.revokeObjectURL(url));
  }, [previews]);

  const clearDraft = () => {
    setSelectedType(CHARGE_TYPES[0]);
    setAmount('');
    setDescription('');
    setFiles([]);
    setPreviews([]);
    setPhotoS3Keys([]);
    setEditingIndex(null);
  };

  const validateDraft = (): string | null => {
    const amountValue = Number(amount);
    if (!Number.isFinite(amountValue) || amountValue <= 0) {
      return 'Enter a valid amount';
    }
    const notes = description.trim();
    if (!notes) {
      return 'Enter charge notes for the guest';
    }
    if (notes.length > 500) {
      return 'Notes must be 500 characters or fewer';
    }
    if (!files.length) {
      return 'Upload at least one evidence photo';
    }
    return null;
  };

  const onDrop = useCallback((acceptedFiles: File[]) => {
    setFiles(prev => [...prev, ...acceptedFiles]);
    setPreviews(prev => [...prev, ...acceptedFiles.map(file => URL.createObjectURL(file))]);
    setPhotoS3Keys(prev => [...prev, ...acceptedFiles.map(() => '')]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.webp'] },
    multiple: true,
  });

  const uploadEvidence = async (evidenceFiles: File[]): Promise<string[]> => {
    const keys: string[] = [];
    for (const file of evidenceFiles) {
      const { data } = await getPresignedUrl({
        variables: {
          fileType: file.type || 'image/jpeg',
          carId: booking.carId || booking.car?.id || booking.id,
        },
      });
      const { url, key } = data.getPresignedUrl;
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', url);
        xhr.setRequestHeader('Content-Type', file.type || 'image/jpeg');
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`Upload failed with status ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(file);
      });
      keys.push(key);
    }
    return keys;
  };

  const buildDraftLine = async (): Promise<SavedLine> => {
    const err = validateDraft();
    if (err) throw new Error(err);
    const keys = [...photoS3Keys];
    for (let index = 0; index < files.length; index += 1) {
      if (!keys[index]) {
        const [uploadedKey] = await uploadEvidence([files[index]]);
        keys[index] = uploadedKey;
      }
    }
    return {
      incidentType: selectedType.value,
      label: selectedType.label,
      amount: Number(Number(amount).toFixed(2)),
      description: description.trim(),
      photoS3Keys: keys.filter(Boolean),
      files: [...files],
    };
  };

  const handleAddAnother = async () => {
    try {
      const line = await buildDraftLine();
      setSavedLines(current => {
        if (editingIndex != null) {
          const next = [...current];
          next[editingIndex] = line;
          return next;
        }
        return [...current, line];
      });
      clearDraft();
    } catch (error: any) {
      toast.error(error.message || 'Could not add charge item');
    }
  };

  const handleEditLine = (index: number) => {
    const line = savedLines[index];
    setEditingIndex(index);
    setSelectedType(CHARGE_TYPES.find(type => type.label === line.label) || CHARGE_TYPES[0]);
    setAmount(line.amount.toFixed(2));
    setDescription(line.description);
    setFiles([...line.files]);
    setPreviews(line.files.map(file => URL.createObjectURL(file)));
    setPhotoS3Keys([...line.photoS3Keys]);
  };

  const handleRemoveLine = (index: number) => {
    setSavedLines(current => current.filter((_, i) => i !== index));
    if (editingIndex === index) {
      clearDraft();
    } else if (editingIndex != null && editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const collectLinesForSubmit = async (): Promise<SavedLine[]> => {
    const lines = [...savedLines];
    if (draftHasContent) {
      const draftLine = await buildDraftLine();
      if (editingIndex != null) {
        lines[editingIndex] = draftLine;
      } else {
        lines.push(draftLine);
      }
    }
    return lines;
  };

  const handleSubmit = async () => {
    try {
      const lines = await collectLinesForSubmit();
      if (!lines.length) {
        toast.error('Add at least one charge item');
        return;
      }

      const { data } = await chargeIncidentFees({
        variables: {
          input: {
            bookingId: booking.id,
            lineItems: lines.map(line => ({
              incidentType: line.incidentType,
              amount: line.amount,
              description: line.description,
              photoS3Keys: line.photoS3Keys,
            })),
          },
        },
      });
      await refetchBooking();
      const chargeResult = data?.chargeIncidentFees;
      const total = lines.reduce((sum, line) => sum + line.amount, 0);

      if (chargeResult?.status === 'CHARGED_SUCCESSFULLY') {
        toast.success(
          `Guest charged $${total.toFixed(2)} for ${lines.length} item(s). Receipt emailed.`,
        );
        onClose();
        return;
      }

      setResult(chargeResult);

      if (chargeResult?.status === 'INVOICE_SENT') {
        toast.info(
          `Could not charge the saved card. Invoice for $${total.toFixed(2)} sent to guest.`,
        );
      } else {
        toast.error(chargeResult?.message || 'Charge failed. Please try again.');
      }
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit incident charge');
    }
  };

  const copyPaymentLink = async () => {
    if (!result?.paymentLink) return;
    try {
      await navigator.clipboard.writeText(result.paymentLink);
      toast.success('Payment link copied');
    } catch {
      toast.error('Could not copy payment link');
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalWidth="min(680px, calc(100vw - 20px))">
      <Container>
        <Title>Charge for Incidents</Title>
        {result ? (
          result.status === 'INVOICE_SENT' ? (
            <InvoiceSentBox>
              <strong>Invoice sent to guest</strong>
              <span>{result.message}</span>
              {shouldExposeIncidentPaymentLink(result) && (
                <>
                  <a href={result.paymentLink} target="_blank" rel="noreferrer">
                    Open payment link
                  </a>
                  <Button text="Copy payment link" theme="outlined" onClick={copyPaymentLink} />
                </>
              )}
              <Button text="Done" onClick={onClose} />
            </InvoiceSentBox>
          ) : (
            <ResultBox>
              <strong>Charge failed</strong>
              <span>{result.message || 'Please try again.'}</span>
              <Button text="Close" onClick={() => setResult(null)} />
            </ResultBox>
          )
        ) : (
          <>
            {savedLines.length > 0 && (
              <div tw="flex flex-col gap-2">
                <strong>Line items</strong>
                {savedLines.map((line, index) => (
                  <LineCard key={`${line.label}-${index}`}>
                    <LineRow>
                      <div>
                        <div tw="font-medium">{line.label}</div>
                        <div tw="text-sm text-text-secondary">
                          ${line.amount.toFixed(2)} · {line.description}
                        </div>
                      </div>
                      <div tw="flex gap-2">
                        <Button
                          text="Edit"
                          theme="outlined"
                          onClick={() => handleEditLine(index)}
                        />
                        <Button
                          text="Remove"
                          theme="outlined"
                          onClick={() => handleRemoveLine(index)}
                        />
                      </div>
                    </LineRow>
                  </LineCard>
                ))}
                <div tw="text-right font-medium">Total: ${savedTotal.toFixed(2)}</div>
              </div>
            )}

            {editingIndex != null && (
              <div tw="text-sm text-text-secondary">Editing line {editingIndex + 1}</div>
            )}

            <Grid>
              {CHARGE_TYPES.map(type => (
                <TypeButton
                  key={`${type.label}-${type.value}`}
                  selected={selectedType.label === type.label}
                  onClick={() => setSelectedType(type)}
                >
                  {type.label}
                </TypeButton>
              ))}
            </Grid>

            <label>
              <span tw="block text-sm font-medium text-text-secondary mb-1">Amount</span>
              <Input
                value={amount}
                onChange={event => setAmount(event.target.value.replace(/[^0-9.]/g, ''))}
                placeholder="$0.00"
              />
            </label>

            <label>
              <span tw="block text-sm font-medium text-text-secondary mb-1">
                Description / Notes
              </span>
              <TextArea
                value={description}
                onChange={event => setDescription(event.target.value)}
                maxLength={500}
                placeholder="Describe the charge. This is shown to the guest."
              />
            </label>

            {selectedType.value === InvoiceType.LateFee && booking.actualReturnAt && (
              <div tw="rounded-lg border border-border-default bg-bg-secondary p-3 text-sm text-text-secondary flex flex-col gap-2">
                <strong>Actual return evidence</strong>
                <span>Actual return: {new Date(booking.actualReturnAt).toLocaleString()}</span>
                <span>Late hours: {booking.lateReturnHours || 0}</span>
              </div>
            )}

            <DropzoneContainer {...getRootProps()} isDragActive={isDragActive}>
              <input {...getInputProps()} />
              <p style={{ margin: 0, fontWeight: 600 }}>
                {isDragActive ? 'Drop evidence here' : 'Click or drag evidence photos'}
              </p>
            </DropzoneContainer>

            {!!previews.length && (
              <PhotoGrid>
                {previews.map((preview, index) => (
                  <PhotoPreview key={preview}>
                    <PhotoImg src={preview} alt={`Evidence ${index + 1}`} />
                    <RemoveButton
                      onClick={() => {
                        URL.revokeObjectURL(preview);
                        setFiles(current => current.filter((_, i) => i !== index));
                        setPreviews(current => current.filter((_, i) => i !== index));
                        setPhotoS3Keys(current => current.filter((_, i) => i !== index));
                      }}
                    >
                      x
                    </RemoveButton>
                  </PhotoPreview>
                ))}
              </PhotoGrid>
            )}

            <HelperText>
              We&apos;ll charge the guest&apos;s saved card on file. If that fails, the guest will
              receive one payment link for the full amount.
            </HelperText>

            <Actions>
              <Button
                text={editingIndex != null ? 'Update line item' : 'Add another charge'}
                theme="outlined"
                onClick={handleAddAnother}
                isDisabled={charging}
              />
              <Button
                text={
                  projectedTotal > 0 ? `Charge guest $${projectedTotal.toFixed(2)}` : 'Charge guest'
                }
                onClick={handleSubmit}
                isLoading={charging}
                isDisabled={charging || (savedLines.length === 0 && !draftHasContent)}
              />
            </Actions>
          </>
        )}
      </Container>
    </Modal>
  );
};
